import { along } from '@turf/along';
import { length } from '@turf/length';
import type { Feature, FeatureCollection, Geometry, LineString } from 'geojson';
import { TrafikenConfig } from '../../TrafikenConfig';
import { AjaxSource } from '../AjaxSource';
import type { AjaxSourceOptions } from '../Sources.types';

const defaultOptions = (parkingType: string) => {
    return {
        id: `parking-sth-${parkingType}`,
        url: `${TrafikenConfig.infoLayerServiceBaseUrl}/api/parkings/?type=${parkingType}&region=vst`,
    };
};

export interface ParkingSthlmProperties {
    address?: string;
    citation: string;
    cityDistrict: string;
    dayType: string;
    district?: string;
    endDay?: number;
    endMonth?: number;
    endTime: number;
    extentNo: string;
    extraInfo?: string;
    featureObjectId: string;
    featureVersionId: string;
    fid: number;
    iconId: number;
    maxHours: string;
    maxMinutes: string;
    otherInfo: string;
    parkingBan?: string;
    parkingCost?: string;
    parkingDistrict: string;
    startDay?: number;
    startMonth?: number;
    startTime: number;
    startWeekday: number;
    streetName: string;
    timeLimit?: string;
    timeLimitDescription?: string;
    validFrom: string;
    vehicleLength?: string;
    vfCapacity: string;
    vfMeter: string;
    vfPlatsTyp: string;
}

export class ParkingsSthlmBase extends AjaxSource {
    _iconId: number;
    constructor(parkingType: string, iconId?: number, options?: AjaxSourceOptions) {
        super({ ...defaultOptions(parkingType), ...options });

        this._iconId = iconId;
    }

    parseData(data: FeatureCollection<Geometry>): Feature[] {
        return this.safeMapFeatures(data.features, (x) => {
            if (!x.geometry || !x.properties) {
                console.warn(`[${this.options.id}] Skipping feature without geometry or properties:`, x.id);
                return null;
            }
            const line = x.geometry as LineString;
            const geometryLength = length(x);
            const midPoint = along(line, geometryLength / 2);
            const feature = {
                id: x.id,
                type: x.type,
                geometry: midPoint.geometry,
                properties: {
                    fid: x.properties.fid,
                    address: x.properties.ADDRESS,
                    citation: x.properties.CITATION,
                    cityDistrict: x.properties.CITY_DISTRICT,
                    dayType: x.properties.DAY_TYPE,
                    district: x.properties.CITY_DISTRICT,
                    endDay: x.properties.END_DAY,
                    endMonth: x.properties.END_MONTH,
                    endTime: x.properties.END_TIME,
                    extentNo: x.properties.EXTENT_NO,
                    featureObjectId: x.properties.FEATURE_OBJECT_ID,
                    featureVersionId: x.properties.FEATURE_VERSION_ID,
                    iconId: this._iconId,
                    maxHours: x.properties.MAX_HOURS,
                    maxMinutes: x.properties.MAX_MINUTES,
                    otherInfo: x.properties.OTHER_INFO,
                    parkingBan: x.properties.OTHER_INFO,
                    parkingDistrict: x.properties.PARKING_DISTRICT,
                    startTime: x.properties.START_TIME,
                    startDay: x.properties.START_DAY,
                    startMonth: x.properties.START_MONTH,
                    startWeekday: x.properties.START_WEEKDAY,
                    streetName: x.properties.STREET_NAME,
                    validFrom: x.properties.VALID_FROM,
                    vehicleLength: x.properties.VF_METER,
                    vfCapacity: x.properties.VF_PLATSER,
                    vfMeter: x.properties.VF_METER,
                    vfPlatsTyp: x.properties.VF_PLATS_TYP,
                },
            };
            return feature;
        });
    }
}
